// Access layer + pure helpers for the registry_ignores table.
//
// registry_ignores tracks values the user has explicitly dismissed from
// a project's glossary or attendee registry so they are never re-suggested.
//
// Pure helpers (normalizeIgnoreKey, filterIgnored) carry no Supabase import
// so they unit-test trivially. Supabase calls are confined to the three async
// functions at the bottom.

import { supabase } from "./supabase";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IgnoreKind = "glossary" | "attendee";

export interface IgnoreRow {
  id: string;
  project_id: string;
  kind: IgnoreKind;
  value_normalized: string;
  value_display: string;
}

// ---------------------------------------------------------------------------
// Pure helpers — no Supabase dependency, unit-testable without mocking
// ---------------------------------------------------------------------------

/** Normalise a value to the canonical ignore-key form: trimmed + lowercased. */
export function normalizeIgnoreKey(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Filter `items`, keeping only those whose key (via `key(item)`) is NOT
 * present in `ignored` (after normalisation).
 */
export function filterIgnored<T>(
  items: T[],
  key: (t: T) => string,
  ignored: Set<string>,
): T[] {
  if (ignored.size === 0) return items;
  return items.filter((item) => !ignored.has(normalizeIgnoreKey(key(item))));
}

// ---------------------------------------------------------------------------
// Supabase access
// ---------------------------------------------------------------------------

/** Load all ignored entries for a project + kind. Returns an empty array on
 *  a missing result so callers can safely spread into a Set without null
 *  checks. */
export async function loadIgnores(
  projectId: string,
  kind: IgnoreKind,
): Promise<IgnoreRow[]> {
  const { data, error } = await supabase
    .from("registry_ignores")
    .select("id, project_id, kind, value_normalized, value_display")
    .eq("project_id", projectId)
    .eq("kind", kind);
  if (error) throw error;
  return (data ?? []) as IgnoreRow[];
}

/**
 * Add a value to the ignore list for a project + kind.
 *
 * Convention: plain insert that swallows a 23505 unique-violation so that
 * re-ignoring an already-ignored value is silently idempotent — matching the
 * pattern used in SpeakerOverride.tsx, ProposedGlossaryCard.tsx, and
 * ProposedAttendeesCard.tsx throughout this codebase.
 */
export async function addIgnore(
  workspaceId: string,
  projectId: string,
  kind: IgnoreKind,
  valueDisplay: string,
): Promise<void> {
  const { error } = await supabase.from("registry_ignores").insert({
    workspace_id: workspaceId,
    project_id: projectId,
    kind,
    value_normalized: normalizeIgnoreKey(valueDisplay),
    value_display: valueDisplay,
  });
  if (error) {
    // 23505 = unique violation — already ignored. Treat as success.
    if (error.code !== "23505") throw error;
  }
}

/**
 * Remove an ignore entry by id (i.e. "un-ignore" a previously dismissed
 * value, making it eligible for suggestion again).
 */
export async function removeIgnore(id: string): Promise<void> {
  const { error } = await supabase
    .from("registry_ignores")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

/**
 * Load both glossary and attendee ignore rows for a project in one call.
 * DRYs the repeated Promise.all([loadIgnores(pid,"glossary"), loadIgnores(pid,"attendee")])
 * pattern used in MeetingReview and ProjectDetail.
 */
export async function loadBothIgnores(
  projectId: string,
): Promise<{ glossary: IgnoreRow[]; attendee: IgnoreRow[] }> {
  const [glossary, attendee] = await Promise.all([
    loadIgnores(projectId, "glossary"),
    loadIgnores(projectId, "attendee"),
  ]);
  return { glossary, attendee };
}
